/* eslint-disable react/no-did-update-set-state,no-param-reassign */
import React, { Component } from 'react';
import { Row, Column } from 'hedron';
import { observer, inject } from 'mobx-react';
import root from 'window-or-global';
import Parser from 'blocks/Parser/Parser';
import moment from 'moment';
import styled from 'styled-components';
import ds from 'designsystem';
import { media } from 'common/responsive';
import { underscoresToDashes } from 'common/methods/string';
import Container from 'blocks/Container/Container';
import Fade from 'react-reveal/Fade';
import { FADE_VALUES } from 'animations/Animations';
import Paragraph from 'elements/Paragraph';
import Header1 from 'elements/Header1';
import Header5 from 'elements/Header5';
import Navigation from 'blocks/Navigation';
import ShareMechanism from 'blocks/ShareMechanism';
import Newsletter from 'kentico-blocks/Newsletter';
import InjectMetaTags from 'blocks/InjectMetaTags';
import SplashScreen from 'blocks/SplashScreen';
import ArticleCategory from 'pages/BlogArticle/ArticleCategory';
import PageNotFound from 'pages/PageNotFound';
import { generateMetaTagsFromBlogArticle } from 'common/methods/blogMethods';
import NavigationAwarePageContent from 'blocks/NavigationAwarePageContent';
import ANCHOR from 'common/consts/anchor';
import BlogAuthor from './BlogAuthor';
import BlogArticleBox from '../Blog/BlogArticleBox';

/* eslint-disable react/prop-types */

const StyledContainer = styled(Container)`
  ${media.desktop`
    padding-right:${ds.get('metrics.xxl')};
    overflow: hidden;
  `};
`;

const StyledTitle = Header1.extend`
  padding: 0 ${ds.get('metrics.s')};
  font-size: ${ds.fontSize('l')};
  line-height: ${ds.get('type.lineHeight.l')};
  
  ${media.tablet`
    font-size: ${ds.fontSize('xl')};
    line-height: ${ds.get('type.lineHeight.xl')};
  `}
  
  ${media.desktop`
    font-size: ${ds.fontSize('xxl')};
    line-height: ${ds.get('type.lineHeight.xxl')};
`};
`;

const StyledHeader5 = styled(Header5)`
  margin: ${ds.get('metrics.m')} 0 ${ds.get('metrics.base')};
  margin-left: ${({ alignLeft }) => (alignLeft ? ds.get('metrics.s') : '')};
  ${media.desktop`
    margin: ${ds.get('metrics.xxl')} 0 ${ds.get('metrics.l')};
  `};
`;

const StyledParagraph = styled(Paragraph)`
  margin: 0;
  ${media.desktop`
    margin-top:${ds.get('metrics.base')};
  `};
`;

const MarginContainer = styled(Container)`
  margin-top: ${ds.get('metrics.xxl')};

  ${media.desktop`
    margin-top: 250px;
  `}
`;

const PaddingContainer = styled(Container)`
  ${media.desktop`
    padding: ${ds.get('metrics.s')};
  `};
`;


const StyledColumn = styled(Column)`
  margin-top:-${ds.get('metrics.base')};
  ${media.desktop`
    margin-top:0;
  `};
`;

const LoadArticles = styled(Paragraph)`
  margin: ${ds.get('metrics.base')} auto;
  width:100px;
  cursor:pointer;
  text-decoration: underline;
`;

const publicationDateFormat = 'DD.MM/YYYY';

@inject('coreStore', 'windowStore', 'blogStore', 'appStore')
@observer
export default class BlogArticle extends Component {
  state = {
    blogArticle: null,
    isArticleFound: true,
  };

  async componentDidMount() {
    const { blogStore } = this.props;
    root.scrollTo(0, 0);

    await blogStore.initialContentFetch();

    const {
      match: {
        params: {
          slug,
          category,
        },
      },
    } = this.props;

    const blogArticle = await blogStore.getArticleBySlug(slug);

    if (!blogArticle || category !== underscoresToDashes(blogArticle.mainCategory.codename)) {
      this.setState({ isArticleFound: false });
    } else {
      this.setState({
        blogArticle,
      });
    }
  }

  async componentDidUpdate(props) {
    const {
      blogStore,
      history: {
        location: {
          state,
        },
      },
    } = props;

    const {
      blogArticle,
    } = this.state;

    if (blogArticle && blogArticle.slug && state) {
      const incomingSlug = state.slug;
      if (blogArticle.slug !== incomingSlug) {
        const newBlogArticle = await blogStore.getArticleBySlug(incomingSlug);
        this.setState({
          blogArticle: newBlogArticle,
        });
        blogStore.resetArticlesCountinArticleView();
        root.scrollTo(0, 0);
      }
    }
  }

  componentWillUnmount() {
    const { blogStore } = this.props;
    blogStore.resetArticlesCountinArticleView();
  }

  render() {
    const { blogArticle, isArticleFound } = this.state;

    if (!isArticleFound) {
      return <PageNotFound />;
    }
    if (!blogArticle) {
      return <SplashScreen />;
    }

    const {
      blogArticle: {
        id,
        title,
        mainCategory,
        content,
        articleAuthor,
        publicationDate,
        linkToProduct: {
          slug,
        },
      },
    } = this.state;

    const {
      blogStore,
      coreStore: {
        isLoaded,
        translations,
        content: {
          newsletter,
        },
        windowStore,
      },
      appStore: {
        currentLanguage,
      },
    } = this.props;

    const metaTags = generateMetaTagsFromBlogArticle(blogArticle);

    return (
      <React.Fragment>
        <InjectMetaTags {...metaTags} />
        <Navigation />
        <NavigationAwarePageContent>
          <MarginContainer>
            {slug && (!windowStore.isOnTablet) && (
              <ArticleCategory
                slug={`${ANCHOR.BLOG[currentLanguage]}/${underscoresToDashes(mainCategory.codename)}`}
                description={blogStore.translateBlogCategory(mainCategory.codename)}
                onClick={() => blogStore.setBlogFilter(mainCategory.codename)}
              />
            )}
            <Fade bottom distance={`${FADE_VALUES.BOX.LIGHT}`}>
              <StyledTitle>
                {title}
              </StyledTitle>
            </Fade>
          </MarginContainer>

          <StyledContainer>
            <Row>
              <Column lg={2}>
                <StyledParagraph>
                  {moment(publicationDate).format(publicationDateFormat)}
                </StyledParagraph>
                <ShareMechanism />
              </Column>
              <StyledColumn lg={9} lgShift={1}>
                <Parser blogArticle htmlBody={content} />

                {slug && (windowStore.isOnTablet) && (
                  <ArticleCategory
                    slug={ANCHOR.BLOG[currentLanguage]}
                    description={blogStore.translateBlogCategory(mainCategory.codename)}
                    onClick={() => blogStore.setBlogFilter(mainCategory.codename)}
                  />
                )}

                <StyledHeader5 bold>
                  {translations.BLOG_ABOUT_THE_AUTHOR}
                </StyledHeader5>

                {articleAuthor && (
                  <BlogAuthor author={articleAuthor} />
                )}

              </StyledColumn>
            </Row>
          </StyledContainer>
          <PaddingContainer>
            <StyledHeader5 alignLeft bold>
              {translations.BLOG_OTHER_ARTICLES}
            </StyledHeader5>
            <Row divisions={11}>
              {blogStore.getArticlesDetailsForArticleView(id).map((article, index) => (
                <Column
                  lg={3}
                  lgShift={index % 3 ? 1 : 0}
                  fluid={!windowStore.isOnTablet}
                  key={article.system.id}
                >
                  <BlogArticleBox article={article} />
                </Column>
              ))}
            </Row>
            {!blogStore.allArticlesInArticleViewLoaded && (
              <LoadArticles onClick={blogStore.loadMoreArticlesinArticleView}>
                {translations.BLOG_MORE_ARTICLES}
              </LoadArticles>
            )}
            {isLoaded && <Newsletter margin={false} content={newsletter} /> }
          </PaddingContainer>

        </NavigationAwarePageContent>
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/BlogArticle/index.jsx